import { type UseMutationOptions, useMutation } from '@tanstack/react-query';
import type { ImageView, UploadBenchmarkImageBody } from '../../generated';
import { uploadBenchmarkImageV1WorkspacesWorkspaceIdBenchmarksBenchmarkIdImagesUploadPost } from '../../generated';

/*
 * POST /v1/workspaces/{workspace_id}/benchmarks/{benchmark_id}/images/upload, live behind the API's
 * `custom-benchmarks` flag. It creates the image record and answers with a presigned S3 POST; the caller then sends
 * the file to S3 with those fields.
 *
 * `product_category_id` is only sent when the caller has one. Without it the API stores the original as is (JPG,
 * PNG or WEBP, judged by the file name's extension) and neither scores nor resizes it until it is first scored.
 * Tests answer this path with `tests/mocks/benchmark-handlers.ts`.
 */

export type UploadBenchmarkImageMutationVariables = {
  workspace_id: string;
  benchmark_id: string;
} & UploadBenchmarkImageBody;

/** Where and how to send the file: the new image's ID and the presigned S3 POST. */
export interface BenchmarkImageUpload {
  imageId: string;
  /** When the API created the record, by its own clock; what it does to the benchmark later is dated after this. */
  createdAt: string | null;
  uploadUrl: string;
  uploadFields: Record<string, string>;
}

export type UploadBenchmarkImageMutation = BenchmarkImageUpload;

export function toBenchmarkImageUpload(image: ImageView): BenchmarkImageUpload {
  if (!image.id || !image.url || !image.upload_fields) {
    throw new Error('The benchmark image upload response is missing its presigned upload.');
  }
  return {
    imageId: image.id,
    createdAt: image.created_at ?? null,
    uploadUrl: image.url,
    uploadFields: JSON.parse(image.upload_fields) as Record<string, string>,
  };
}

async function uploadBenchmarkImage({
  workspace_id,
  benchmark_id,
  source = 'user_uploaded',
  ...body
}: UploadBenchmarkImageMutationVariables) {
  const { data } = await uploadBenchmarkImageV1WorkspacesWorkspaceIdBenchmarksBenchmarkIdImagesUploadPost({
    path: { workspace_id, benchmark_id },
    body: { ...body, source },
    throwOnError: true,
  });
  return toBenchmarkImageUpload(data);
}

export const useUploadBenchmarkImageMutation = <TError = unknown, TContext = unknown>(
  options?: Omit<
    UseMutationOptions<UploadBenchmarkImageMutation, TError, UploadBenchmarkImageMutationVariables, TContext>,
    'mutationKey' | 'mutationFn'
  >,
) => {
  return useMutation<UploadBenchmarkImageMutation, TError, UploadBenchmarkImageMutationVariables, TContext>({
    mutationKey: ['UploadBenchmarkImage'],
    mutationFn: uploadBenchmarkImage,
    ...options,
  });
};

useUploadBenchmarkImageMutation.fetcher = (variables: UploadBenchmarkImageMutationVariables) => () =>
  uploadBenchmarkImage(variables);
